import { create } from '@bufbuild/protobuf';
import { createContextValues } from '@connectrpc/connect';
import { getAccountMember } from '@qdrant/qdrant-cloud-public-api/account/v1/connect-query';
import {
  type GetAuthenticatedUserResponse,
  type GetUserProfileResponse,
  GetUserProfileResponseSchema,
  IAMService,
  type ListUserMfaMethodsResponse,
  UpdateUserRequestSchema,
  type User,
  type UserMfaMethod,
  UserMfaMethodType,
} from '@qdrant/qdrant-cloud-public-api/iam/v1';
import {
  recordUserConsent,
  logoutUser,
  listPermissions,
  listEffectivePermissions,
  getUserConsent,
  listRoles,
  listUsers,
  updateUser,
  getAuthenticatedUser,
  assignUserRoles,
  createRole,
  deleteRole,
  deleteUser,
  updateRole,
  listUserRoles,
  getUserProfile,
  updateUserProfile,
  listUserMfaMethods,
  updateUserMfaMethod,
  deleteUserMfaMethod,
} from '@qdrant/qdrant-cloud-public-api/iam/v1/connect-query';
import { listUsersWithRoles } from '@qdrant/qdrant-cloud-public-api/ui/v1/ui/connect-query';
import { type QueryClient } from '@tanstack/react-query';
import { isFailedPreconditionError, isNotFoundError } from '@/api/errors';
import { keepAliveTransport, transport } from '@/api/transport';
import {
  createConnectQueryKey,
  createServiceQueries,
  type MutationFactory,
  queryOptions,
  useMutation,
} from '@/connect-query';
import { ctxKeyQueryClient } from '@/connect-query/context-keys';

export const iamService = createServiceQueries(IAMService, {
  getAuthenticatedUserQuery: () => queryOptions(getAuthenticatedUser, undefined, { select: (d) => d.user, transport }),
  getUserConsentQuery: (params) => queryOptions(getUserConsent, params, { select: (d) => d.userConsent, transport }),
  getUserProfileQuery: () => ({
    ...queryOptions(getUserProfile, undefined, { select: (d) => d.userProfile, transport }),
    // A missing profile is expected for users who haven't completed onboarding yet
    meta: { sentryFilter: (error: unknown) => !isNotFoundError(error) },
  }),
  listEffectivePermissionsQuery: (params) =>
    queryOptions(listEffectivePermissions, params, { select: (d) => d.permissions, transport }),
  listPermissionsQuery: (params) => queryOptions(listPermissions, params, { select: (d) => d.permissions, transport }),
  listRolesQuery: (params) => queryOptions(listRoles, params, { select: (d) => d.items, transport }),
  listUserMfaMethodsQuery: () => queryOptions(listUserMfaMethods, undefined, { transport }),
  listUserRolesQuery: (params) => queryOptions(listUserRoles, params, { select: (d) => d.roles, transport }),
  listUsersQuery: (params) => queryOptions(listUsers, params, { select: (d) => d.items, transport }),
});

export const useUpdateUserProfileMutation: MutationFactory<typeof updateUserProfile> = () =>
  useMutation(updateUserProfile, {
    onSuccess: ({ userProfile }, _variables, _onMutateResult, { client }) => {
      const userProfileQueryKey = createConnectQueryKey({ schema: getUserProfile });
      // Update the authenticated user data in the cache
      client.setQueryData<GetUserProfileResponse>(userProfileQueryKey, (input) =>
        input
          ? { ...input, userProfile: { ...input.userProfile, ...userProfile } }
          : create(GetUserProfileResponseSchema, { userProfile }),
      );
    },
  });

export const useRecordUserConsentMutation: MutationFactory<typeof recordUserConsent> = () =>
  useMutation(recordUserConsent, {
    onSuccess: (_data, input, _onMutateResult, { client }) =>
      client.invalidateQueries({
        queryKey: createConnectQueryKey({ input, schema: getUserConsent }),
      }),
  });

export const useAssignUserRolesMutation: MutationFactory<typeof assignUserRoles> = () =>
  useMutation(assignUserRoles, {
    onSuccess: (_data, { accountId, userId }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            input: { accountId, userId },
            schema: getAccountMember,
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            input: { accountId },
            schema: listRoles,
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            input: { accountId },
            schema: listUsersWithRoles,
          }),
        }),
        router.invalidate({ filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId' }),
      ]),
  });

export const useCreateRoleMutation: MutationFactory<typeof createRole> = () =>
  useMutation(createRole, {
    onSuccess: (_data, { role: { accountId } }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ input: { accountId }, schema: listRoles }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId/cloud-access/roles',
        }),
      ]),
  });

export const useDeleteRoleMutation: MutationFactory<typeof deleteRole> = () =>
  useMutation(deleteRole, {
    onSuccess: (_data, { accountId }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ input: { accountId }, schema: listRoles }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId/cloud-access/roles',
        }),
      ]),
  });

export const useUpdateRoleMutation: MutationFactory<typeof updateRole> = () =>
  useMutation(updateRole, {
    onSuccess: (_data, { role: { accountId } }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            input: { accountId },
            schema: listRoles,
          }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId/cloud-access/roles',
        }),
      ]),
  });

export const useUpdateUserMutation: MutationFactory<typeof updateUser> = (options) =>
  useMutation(updateUser, {
    onSuccess: ({ user }, _variables, _onMutateResult, { client }) => {
      const authenticatedUserQueryKey = createConnectQueryKey({ schema: getAuthenticatedUser });
      // Update the authenticated user data in the cache
      client.setQueryData<GetAuthenticatedUserResponse>(authenticatedUserQueryKey, (input) =>
        input ? { ...input, user } : input,
      );
    },
    ...options,
  });

export const useDeleteUserMutation: MutationFactory<typeof deleteUser> = () =>
  useMutation(deleteUser, {
    // The account transfer/delete calls just ahead of this one resolve before the backend's
    // ownership index has necessarily caught up, so a `FailedPrecondition` ("user owns accounts")
    // right after them is a race, not a real block — retry briefly before surfacing it.
    retry: (failureCount, error) => isFailedPreconditionError(error) && failureCount < 3,
    retryDelay: (failureCount) => (failureCount + 1) * 500,
  });

export const useUpdateUserMfaMethodMutation: MutationFactory<typeof updateUserMfaMethod> = () =>
  useMutation(updateUserMfaMethod, {
    onSuccess: ({ mfaMethod }, _variables, _onMutateResult, { client }) => {
      const listUserMfaMethodsQueryKey = createConnectQueryKey({ schema: listUserMfaMethods });

      client.setQueryData(listUserMfaMethodsQueryKey, (input) =>
        input
          ? {
              ...input,
              items: input.items.map((method) => (method.externalId === mfaMethod.externalId ? mfaMethod : method)),
            }
          : input,
      );
    },
  });

export const useDeleteUserMfaMethodMutation: MutationFactory<
  typeof deleteUserMfaMethod,
  { invalidateCache?: boolean }
> = (options) =>
  useMutation(deleteUserMfaMethod, {
    onSettled: async (_data, _error, _variables, mutateResult, { client }) => {
      if (mutateResult?.invalidateCache === false) {
        return;
      }
      await client.invalidateQueries({ queryKey: createConnectQueryKey({ schema: listUserMfaMethods }) });
    },
    ...options,
  });

export const updateUserMutationFn = async ({
  user,
  defaultAccountId,
  client,
}: {
  user: User;
  defaultAccountId: string;
  client: QueryClient;
}) => {
  const updateUserInput = create(UpdateUserRequestSchema, { user: { ...user, defaultAccountId } });
  const contextValues = createContextValues().set(ctxKeyQueryClient, client);

  const data = await transport.unary(updateUser, undefined, undefined, undefined, updateUserInput, contextValues);

  return data.message;
};

export const logoutUserMutationFn = (client: QueryClient) =>
  keepAliveTransport.unary(
    logoutUser,
    undefined,
    undefined,
    undefined,
    {},
    createContextValues().set(ctxKeyQueryClient, client),
  );

export const selectPasskeyUserMfaMethods = (data?: ListUserMfaMethodsResponse): UserMfaMethod[] =>
  data?.items?.filter((item) => item.type === UserMfaMethodType.WEBAUTHN_ROAMING) ?? [];

export const selectTOTPUserMfaMethod = (data?: ListUserMfaMethodsResponse): UserMfaMethod | undefined =>
  data?.items?.find((item) => item.type === UserMfaMethodType.TOTP);

export const selectRecoveryCodeUserMfaMethod = (data?: ListUserMfaMethodsResponse): UserMfaMethod | undefined =>
  data?.items?.find((item) => item.type === UserMfaMethodType.RECOVERY_CODE);
